<template>
    <div class="card card-height">
        <div class="table-responsive">
            <table class="table custom-table-padding">
                <thead>
                    						
                    <tr>
                        <th>Photo</th>
                        <th class="patient-details">Patient Details</th>
                        <th class="admission-no">
                            <span class="d-block">Admission No |</span>
                            <span class="d-block">Date & Time</span>
                        </th>
                        <th>Ward/Bed/Cabin</th>
                        <th>
                            <span class="d-block">Consultant</span>
                            <span>(Reffered doctor)</span>
                        </th>
                        <th>Admitted By</th>
                        <th class="action-th">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in tableItems" :key="i">

                        <td>
                            <img :src=item.patient_photo alt="avatar" class="profile-pic">
                        </td>

                        <td> 
                            <div class="col">
                                <strong>
                                    <h4>{{ item.patient_name }} {{ formatPatientInfo(item) }} </h4>
                                </strong>
                                {{ formatPatientAdditionalInfo(item) }}
                            </div>
                        </td>

                        <td>
                            <div class="col">
                                <h5 class="text-bold">{{ item.admission_no }}</h5>
                               <span>{{ item.admission_date }}</span> 
                            </div>
                        </td>

                        <td>
                            {{ item.bed }}
                        </td>

                        <td>
                            {{ item.consultant }}
                        </td>

                        <td>
                            {{ item.admitted_by }}
                        </td>

                        <td>

                             <div class="action">
                                <button @click="onClickEdit(item.id)" class="btn btn-outline-primary btn-sm me-1" title="View/Edit">
                                    <i class="fas fa-eye"></i>
                                </button>

                                <div class="dropdown d-inline-block">
                                    <button class="btn btn-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                        <i class="fas fa-ellipsis-v"></i>
                                    </button>
                                    <ul class="dropdown-menu">
                                        <li>
                                            <a class="dropdown-item" href="javascript:void(0)" @click="goToPrint(item.id)">
                                                <i class="fas fa-print me-2"></i>Print Dynamic Form
                                            </a>
                                        </li>
                                        <li>
                                            <a class="dropdown-item" href="javascript:void(0)" @click="goToStaticPrint(item.id)">
                                                <i class="fas fa-print me-2"></i>Print Static Form
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                        </td>
                    </tr>
                </tbody>
            </table>
        <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
    </div>
</div>
</template>

<script setup>
    import { ref } from 'vue'
    import { useRoute, useRouter } from "vue-router";
    import { useStore } from 'vuex';
    import { dateOfBirthConvertToAge } from '@/services/utils/global.js'
    import handleHospital from '@/services/modules/hospital'
    import useDate from '@/services/utils/day';
    import handleCompany from "@/services/modules/company";
    import handleBarcode from "@/services/modules/barcode";
    import ipdAdmission from '@/services/utils/pdf/ipdAdmission';
    import ipdStaticAdmission from '@/services/utils/pdf/ipdStaticAdmission';

    const $route = useRoute();
    const $router = useRouter();
    const store = useStore();
    import { lowerCase } from 'lodash'
    const props = defineProps({
        tableItems: {
            type: Array,
        }
    })

    const formData = ref({});
    const companyInfo = ref({});

    const { fetchSingleNote } = handleHospital();
    const { formatDate } = useDate();
    const { fetchCompanyInfo } = handleCompany();
    const { fetchBarcode } = handleBarcode();
    const { exportToPDF } = ipdAdmission();
    const { exportToStaticPDF } = ipdStaticAdmission();

    const goToPrint = async(id) => {
        let barcodeAdmission = null;
        let barcodePatient = null;
        let qrCode = null;
        const userName = store.state.user.user.name;

        await getSingleIpdRegister(id);
        await getCompanyInfo();

        let qrCodeData = JSON.stringify({
            ref_no: formData.value.admission_no,
            ref_date: formData.value.admission_date,
            created_by: userName,
            created_date_time: formatDate(new Date())
        })

        var barCodeQuery = `?code=${formData.value.patient.serial_no}&qrcode=${qrCodeData}`;
        var barCodeAdmissionQuery = `?code=${formData.value.admission_no}`;

        await Promise.all([
            fetchBarcode(barCodeQuery).then( (res) => {
                barcodePatient = res.barcode
                qrCode = res.qrcode;
            }),
            fetchBarcode(barCodeAdmissionQuery).then( (res) => {
                barcodeAdmission = res.barcode;
            })
        ]).then(() => {
            exportToPDF(companyInfo.value, formData.value, qrCode, barcodePatient, barcodeAdmission)
        }).catch((e) => {
            console.log('Something went wrong');
        })
    }

    const goToStaticPrint = async(id) => {
        await getSingleIpdRegister(id);
        await getCompanyInfo();

        try {
            exportToStaticPDF(companyInfo.value, formData.value);
        } catch (e) {
            console.log('Something went wrong');
        }
    }

    const onClickEdit = (id) => {
        $router.push({name: 'edit-ipd-admission',
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId,
                admissionId: id
            },
            query: $route.query,
        })
    }

    const formatPatientInfo = (item) => {
        let text = '';
        if(!item.patient_birthday && !item.patient_gender) return text;

        if(item.patient_birthday){
            const { year } = dateOfBirthConvertToAge(item.patient_birthday);
            text =+ year + 'years';
        }

        if(item.patient_birthday && item.patient_gender) text += ', ';

        if(item.patient_gender){
            const gender = lowerCase(item.patient_gender);
            text += `${gender}`;
        }
        
        return `(${text})`;
    }

    const formatPatientAdditionalInfo = (item) => {
        let text = '';
        if(item.mobile) text += item.mobile;
        if((item.mobile && item.id)) text += ' | ';
        if(item.serial_no) text += item.serial_no;
        return text;
    }

    const getSingleIpdRegister = async (id) => {
        let query = `?company_id=${$route.params.companyId}&search_type=all`;

        await fetchSingleNote(query, id).then((res) => {
            if (!res.status) return;
            formData.value = res.data;
        })
    }

    const getCompanyInfo = async () => {
        await fetchCompanyInfo($route.params.companyId).then((res) => {
            companyInfo.value = res.data
        })
    }
</script>

<style scoped>

.admission-no{
    width: 15%;
}
.patient-details{
    width: 25%;
}
.print-btn{
    margin-right: 5px;
}
.action-th{
    width: 15%;
    text-align: center;
}
.action{
    text-align: center;
}

.profile-pic{
    width: 65px;
}
</style>